import { ReactNode } from "react";

import { useDroppable } from "@dnd-kit/core";
import { Box } from "@mui/material";

import { DocumentGroup } from "@/api";
import { useDocumentDndContext } from "@/common/components/layout/components/dropzone/dnd/DocumentDndContext";
import { isDropAllowed } from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";

interface Props {
  /** The asset inventory group this upload area represents — also the droppable id. */
  zoneId: DocumentGroup;
  children: ReactNode;
}

/**
 * Drop target wrapping an asset inventory upload area. Dropping a dropzone panel document here
 * queues a pending move in the view's form — persisted on save, reverted on reset.
 */
export function AssetDocumentDropArea({ zoneId, children }: Props) {
  const { activeDrag } = useDocumentDndContext();

  const canDrop = activeDrag !== null && isDropAllowed(activeDrag.sourceZone, zoneId);
  const { setNodeRef, isOver } = useDroppable({ id: zoneId, disabled: !canDrop });

  const showHighlight = canDrop && isOver;
  const showHint = canDrop && !isOver;

  return (
    <Box
      ref={setNodeRef}
      sx={(theme) => ({
        border: `1px solid ${showHighlight ? theme.palette.primary.main : showHint ? theme.palette.primary.light : "transparent"}`,
        borderStyle: showHint ? "dashed" : "solid",
        borderRadius: 0.5,
        backgroundColor: showHighlight ? theme.palette.action.hover : undefined,
        transition: "border 150ms ease, background-color 150ms ease",
      })}
    >
      {children}
    </Box>
  );
}
